import { Box, Chip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { DepartmentAlert } from "./types";
import { DETAIL_COLORS, isCallInDetail, isHppdDetail, isMismatchDetail, isRatioDetail, isWeeklyOvertimeDetail, MISMATCH_REASON_PALETTE_KEYS } from "./utils";

const DetailPill: FC<{ label: string; value: string; color: string; emphasis?: boolean }> = ({ label, value, color, emphasis }) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			px: 1.5,
			py: 0.5,
			borderRadius: "12px",
			bgcolor: alpha(color, 0.08),
			minWidth: 60,
		}}
	>
		<Typography variant="caption" sx={{ color, fontSize: "0.6rem", lineHeight: 1.2, fontWeight: 500, whiteSpace: "nowrap" }}>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ color, fontWeight: emphasis ? 800 : 700, fontSize: emphasis ? "0.95rem" : "0.8rem", lineHeight: 1.3 }}>
			{value}
		</Typography>
	</Box>
);

export const AlertDetailVisual: FC<{ alert: DepartmentAlert; typeColors: { chipBg: string; text: string; iconBg: string } }> = ({ alert, typeColors }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const detail = alert.detail;

	// Resolve mismatch reason colors from the MUI theme via semantic palette keys
	const mismatchReasonColors: Record<string, string> = Object.fromEntries(
		Object.entries(MISMATCH_REASON_PALETTE_KEYS).map(([reason, key]) => [reason, key === "grey" ? theme.palette.grey[600] : theme.palette[key].main]),
	);

	switch (alert.alertType) {
		case "overstaffed": {
			if (isHppdDetail(detail)) {
				return (
					<Box
						sx={{
							display: "flex",
							gap: 1,
							flexWrap: "wrap",
						}}
					>
						<DetailPill label="HPPD" value={`${detail.hppdPercent}%`} color={theme.palette.error.main} emphasis />
						<DetailPill label={t("detail.target")} value={`${detail.maxValidHppdPercent}%`} color={theme.palette.grey[600]} />
						<DetailPill label={t("detail.census")} value={String(detail.census)} color={theme.palette.primary.main} />
						<DetailPill label={t("detail.excess")} value={`${detail.excessCount}`} color={theme.palette.grey[700]} />
					</Box>
				);
			}
			if (isRatioDetail(detail)) {
				return (
					<Box
						sx={{
							display: "flex",
							gap: 1,
							flexWrap: "wrap",
						}}
					>
						<DetailPill label={t("detail.actual")} value={String(detail.actualStaffCount)} color={theme.palette.error.main} emphasis />
						<DetailPill label={t("detail.expected")} value={String(detail.ratioLadderAmount)} color={theme.palette.grey[600]} />
						<DetailPill label={t("detail.census")} value={String(detail.census)} color={theme.palette.primary.main} />
						<DetailPill label={t("detail.excess")} value={`${detail.excessCount}`} color={theme.palette.grey[700]} />
					</Box>
				);
			}
			return null;
		}
		case "call_ins": {
			if (isCallInDetail(detail)) {
				return (
					<Box
						sx={{
							display: "flex",
							gap: 0.5,
							flexWrap: "wrap",
						}}
					>
						{Object.entries(detail.reasons).map(([reason, count]) => (
							<Chip
								key={reason}
								label={`${reason} (${count})`}
								size="small"
								sx={{ bgcolor: typeColors.chipBg, color: typeColors.text, fontWeight: 600, fontSize: "0.7rem", height: 24 }}
							/>
						))}
					</Box>
				);
			}
			return <DetailPill label={t("detail.total")} value={String(alert.count)} color={typeColors.text} emphasis />;
		}
		case "weekly_overtime": {
			if (isWeeklyOvertimeDetail(detail)) {
				return (
					<Box
						sx={{
							display: "flex",
							gap: 1,
							flexWrap: "wrap",
						}}
					>
						<DetailPill label={t("detail.total.ot")} value={`${detail.totalOtHours}h`} color={DETAIL_COLORS.otHours} emphasis />
						<DetailPill label={t("detail.nurses")} value={String(detail.nurseCount)} color={DETAIL_COLORS.nurseCount} />
					</Box>
				);
			}
			return (
				<Box
					sx={{
						display: "flex",
						gap: 1,
						flexWrap: "wrap",
					}}
				>
					<DetailPill label={t("detail.total.ot")} value={`${alert.value}h`} color={DETAIL_COLORS.otHours} emphasis />
					<DetailPill label={t("detail.nurses")} value={String(alert.count)} color={DETAIL_COLORS.nurseCount} />
				</Box>
			);
		}
		case "schedule_vs_actual_mismatch": {
			if (!isMismatchDetail(detail)) return null;
			const reasonCounts: Record<string, number> = {};
			for (const nurse of detail.affectedNurses) {
				reasonCounts[nurse.reason] = (reasonCounts[nurse.reason] ?? 0) + 1;
			}
			return (
				<Box
					sx={{
						display: "flex",
						gap: 1,
						flexWrap: "wrap",
					}}
				>
					{Object.entries(reasonCounts).map(([reason, count]) => (
						<DetailPill key={reason} label={reason} value={String(count)} color={mismatchReasonColors[reason] ?? theme.palette.primary.light} />
					))}
				</Box>
			);
		}
		default:
			return null;
	}
};
